import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { Fragment, useMemo } from 'react';

import { Chip } from '~components/Chip/Chip';
import { EVENT_TIMELINE_JOB_EVENT_TIME_FORMAT } from '~components/EventTimeline/EventTimeline';
import {
  actorName,
  eventContextLine,
  eventHeadline,
  eventMatchedConditions,
  getGroupDotColor,
  groupChangeRowsByParty,
  groupLabel,
  matchedCatchAllLine,
  matchedConditionLabel,
  matchedConditionsLabel,
  MergedPricingEntry,
  PricingChangeRow,
  PricingGroup,
  resolveEffectiveRateTypes,
} from '~components/Job/LoadPricing/pricingEventsHelpers';
import { PricingEvent } from '~hooks/usePricing/models';

const DATE_FORMAT = 'MMM D, YYYY';
const EMPTY_CELL = '—';

interface PricingEventsTableProps {
  entries: MergedPricingEntry[];
  // Resolved rate types keyed by event id. The timeline passes this computed over the FULL deduped
  // list (not the filtered/visible subset) so carry-forward can't be broken by a dropped event. When
  // omitted (e.g. standalone use), it's resolved from the entries' events.
  effectiveRateTypesByEvent?: ReturnType<typeof resolveEffectiveRateTypes>;
}

interface TableRowEntry {
  party: PricingGroup | null;
  row: PricingChangeRow;
  isFirstOfParty: boolean;
  partyRowSpan: number;
}

// Flatten one event's changes into rows. `isFirstOfParty`/`partyRowSpan` let the party cell span its
// change rows, mirroring the "grouped per save" rowspan layout. May be empty (summary-only events).
const changeEntriesForEvent = (
  event: PricingEvent,
  effectiveRateTypesByEvent: ReturnType<typeof resolveEffectiveRateTypes>,
): TableRowEntry[] =>
  groupChangeRowsByParty(
    event.changes ?? [],
    effectiveRateTypesByEvent.get(event.id),
  ).flatMap((group) =>
    group.rows.map((row, index) => ({
      party: group.party,
      row,
      isFirstOfParty: index === 0,
      partyRowSpan: group.rows.length,
    })),
  );

// Extra bottom padding under an event's final change row is the break between events — whitespace
// UNDER the last item, so the next event's date/user stays flush (no gap above it) and the first
// event stays flush to the sticky header. `&&` raises specificity over the table's `& td` py rule.
const lastRowGapSx = { '&&': { pb: 4 } };

/**
 * Tabular view of the deduped pricing events. Each event leads with a summary row (Date/Time/User +
 * the friendly summary spanning the change columns), followed by one row per change, with the party
 * cell spanning its changes.
 */
export default function PricingEventsTable({
  entries,
  effectiveRateTypesByEvent,
}: PricingEventsTableProps) {
  const theme = useTheme();

  const headerCellSx = {
    backgroundColor: theme.palette.background.paper,
    // A slightly stronger rule than the row borders (treadGray7) so the sticky header reads as a
    // header, not just another row, as content scrolls beneath it.
    borderBottom: `1px solid ${theme.brandV2.colors.treadGray5}`,
    color: theme.brandV2.colors.treadGray2,
    fontSize: '12px',
    fontWeight: 700,
    letterSpacing: '0.03em',
    textTransform: 'uppercase',
  };

  // The label-ish cells (regular weight) vs the value cells (medium weight) give the eye a clear
  // label/data split when scanning columns.
  const labelCellSx = {
    color: theme.brandV2.colors.treadGray1,
    fontSize: '12px',
    verticalAlign: 'top',
  };

  // Values wrap (and long unbroken tokens like UUIDs break) so the table fits the panel width
  // without a horizontal scroll.
  const valueCellSx = {
    color: theme.brandV2.colors.treadGray2,
    fontSize: '12px',
    fontWeight: 600,
    overflowWrap: 'anywhere',
    verticalAlign: 'top',
  };

  // Prior value muted, new value emphasized — same before/after treatment as the list view.
  const beforeCellSx = {
    ...valueCellSx,
    color: theme.brandV2.colors.treadGray3,
    fontWeight: 500,
  };
  const afterCellSx = {
    ...valueCellSx,
    color: theme.brandV2.colors.treadBlack,
    fontWeight: 700,
  };

  // The event's derived headline — shown once per event, above its changes (mirrors the list view
  // and our other timelines).
  const summaryCellSx = {
    color: theme.brandV2.colors.treadBlack,
    fontSize: '14px',
    fontWeight: 600,
    verticalAlign: 'top',
  };

  // The cause, muted beneath the headline: lighter and regular weight so the headline stays the
  // thing you scan and this reads as the explanation under it.
  const contextLineSx = {
    color: theme.brandV2.colors.treadGray3,
    fontSize: '12px',
    fontWeight: 400,
  };

  // A quiet uppercase marker (no pill/background) prefixing an add-on row, so the "Add-on" tag is
  // distinct from the add-on's name without adding visual clutter to a long list.
  const addOnMarkerSx = {
    color: theme.brandV2.colors.treadGray4,
    flexShrink: 0,
    fontSize: '10px',
    fontWeight: 600,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
  };

  // Date, time, and user identify the event, so they read larger (14px) and heavier than the 12px
  // change rows beneath them.
  const eventMetaCellSx = { ...labelCellSx, fontSize: '14px', fontWeight: 600 };

  // Prefer the caller's map (resolved over the full history); fall back to the entries in hand.
  const resolvedRateTypes = useMemo(
    () =>
      effectiveRateTypesByEvent ??
      resolveEffectiveRateTypes(entries.map((entry) => entry.event)),
    [effectiveRateTypesByEvent, entries],
  );

  return (
    <Table
      size="small"
      stickyHeader
      sx={{
        width: '100%',
        '& td, & th': { px: 1, py: 1 },
        '& td': { borderColor: theme.brandV2.colors.treadGray7 },
      }}
    >
      <TableHead>
        <TableRow>
          <TableCell sx={headerCellSx}>{t('loads.pricing.table_date')}</TableCell>
          <TableCell sx={headerCellSx}>{t('loads.pricing.table_changed_by')}</TableCell>
          {/* Spans the party sub-column + the change-label column; party is a row sub-group, not its own column. */}
          <TableCell
            colSpan={2}
            sx={{ ...headerCellSx, minWidth: '260px', width: '36%' }}
          >
            {t('loads.pricing.table_event')}
          </TableCell>
          <TableCell sx={{ ...headerCellSx, minWidth: '150px', width: '17%' }}>
            {t('loads.pricing.table_before')}
          </TableCell>
          <TableCell sx={{ ...headerCellSx, minWidth: '150px', width: '17%' }}>
            {t('loads.pricing.table_after')}
          </TableCell>
        </TableRow>
      </TableHead>

      <TableBody>
        {entries.map((entry, eventIndex) => {
          const { event } = entry;
          const contextLine = eventContextLine(event);
          const matchedConditions = eventMatchedConditions(event);
          // No conditions is not no answer: the card that won was a catch-all, which the row says
          // out loud. `null` is the different case — this event names no card at all.
          const matchedCatchAllCard = matchedConditions?.length === 0;
          const changeEntries = changeEntriesForEvent(event, resolvedRateTypes);
          // + 1 for the summary row that leads every event.
          const eventRowSpan = changeEntries.length + 1;
          const isLastEvent = eventIndex === entries.length - 1;
          // Summary-only events (no change rows) carry the inter-event gap on the summary row itself,
          // since there's no last change row to hang it on.
          const summaryRowGapSx =
            changeEntries.length === 0 && !isLastEvent ? lastRowGapSx : undefined;

          return (
            <Fragment key={event.id}>
              <TableRow>
                <TableCell
                  rowSpan={eventRowSpan}
                  sx={{ ...eventMetaCellSx, whiteSpace: 'nowrap', ...summaryRowGapSx }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Box component="span">
                      {dayjs.tz(event.occurredAt).format(DATE_FORMAT)}
                    </Box>
                    <Box
                      component="span"
                      sx={{ color: theme.brandV2.colors.treadGray3, fontWeight: 400 }}
                    >
                      {dayjs
                        .tz(event.occurredAt)
                        .format(EVENT_TIMELINE_JOB_EVENT_TIME_FORMAT)}
                    </Box>
                  </Box>
                </TableCell>
                <TableCell
                  rowSpan={eventRowSpan}
                  sx={{ ...eventMetaCellSx, ...summaryRowGapSx }}
                >
                  {actorName(event)}
                </TableCell>
                <TableCell
                  colSpan={4}
                  sx={{
                    ...summaryCellSx,
                    pb: 1,
                    // No rule under the event title — it should connect to its own change rows.
                    // `&&` beats the table's `& td` border rule; also carries the inter-event gap
                    // for summary-only events (no change row below to hang it on).
                    '&&': { borderBottom: 'none', ...(summaryRowGapSx ? { pb: 4 } : {}) },
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Box component="span">{eventHeadline(entry)}</Box>
                    {Boolean(contextLine) && (
                      <Box component="span" sx={contextLineSx}>
                        {contextLine}
                      </Box>
                    )}
                    {matchedCatchAllCard && (
                      <Box component="span" sx={contextLineSx}>
                        {matchedCatchAllLine(event)}
                      </Box>
                    )}
                    {matchedConditions !== null && matchedConditions.length > 0 && (
                      <Box
                        sx={{
                          alignItems: 'center',
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 0.5,
                          mt: 0.5,
                        }}
                      >
                        <Box component="span" sx={contextLineSx}>
                          {matchedConditionsLabel(event)}
                        </Box>
                        {matchedConditions.map((condition) => (
                          <Chip
                            key={`${condition.type}-${condition.label}`}
                            color="default"
                            label={matchedConditionLabel(condition)}
                            hideDropdownArrow
                          />
                        ))}
                      </Box>
                    )}
                  </Box>
                </TableCell>
              </TableRow>

              {changeEntries.map((entry, index) => {
                // Space the event apart from the next one by padding under its final change row
                // (skipped for the last event, which needs no trailing gap).
                const gapSx =
                  index === changeEntries.length - 1 && !isLastEvent
                    ? lastRowGapSx
                    : undefined;

                return (
                  <TableRow key={`${event.id}_${index}`}>
                    {entry.isFirstOfParty && (
                      <TableCell rowSpan={entry.partyRowSpan} sx={labelCellSx}>
                        {/* Shared/order-level rows (e.g. material rate) aren't party-scoped, so the
                            party cell stays blank rather than showing a misleading em-dash. */}
                        {entry.party && (
                          <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.75 }}>
                            <Box
                              sx={{
                                backgroundColor: getGroupDotColor(theme)[entry.party],
                                borderRadius: '50%',
                                flexShrink: 0,
                                height: '8px',
                                width: '8px',
                              }}
                            />
                            {groupLabel(entry.party)}
                          </Box>
                        )}
                      </TableCell>
                    )}

                    <TableCell
                      sx={{
                        ...labelCellSx,
                        minWidth: '260px',
                        whiteSpace: 'normal',
                        ...gapSx,
                      }}
                    >
                      {entry.row.isAddOn ? (
                        <Box sx={{ alignItems: 'baseline', display: 'flex', gap: 0.75 }}>
                          <Box component="span" sx={addOnMarkerSx}>
                            {t('loads.pricing.add_on')}
                          </Box>
                          <Box component="span">{entry.row.label}</Box>
                        </Box>
                      ) : (
                        entry.row.label
                      )}
                    </TableCell>
                    <TableCell sx={{ ...beforeCellSx, ...gapSx }}>
                      {entry.row.before ?? EMPTY_CELL}
                    </TableCell>
                    <TableCell sx={{ ...afterCellSx, ...gapSx }}>
                      {entry.row.after ?? EMPTY_CELL}
                    </TableCell>
                  </TableRow>
                );
              })}
            </Fragment>
          );
        })}
      </TableBody>
    </Table>
  );
}
